import { type BaseBoxProps } from "@tokenterminal/ui/Box"
import { Link, type LinkProps } from "@tokenterminal/ui/Link"
import { ListItem } from "@tokenterminal/ui/List"
import { ScrollBox } from "@tokenterminal/ui/ScrollBox"
import { Stack, type StackProps } from "@tokenterminal/ui/Stack"
import { withOverflow } from "@tokenterminal/ui/util.css"
import {
  type ReactNode,
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import { asideList, asideListItem, asideListItemLink } from "./AsideList.css"

export const AsideListScrollContainer = forwardRef<
  HTMLElement,
  Pick<BaseBoxProps, "children">
>(function AsideListScrollContainer(props, ref) {
  return (
    <div className="relative h-full contain-strict -mx-4 -my-2">
      <ScrollBox
        as="nav"
        className={withOverflow}
        position="absolute"
        top="0"
        left="0"
        width="100%"
        height="100%"
        ref={ref}
        {...props}
      />
    </div>
  )
})

interface Props extends StackProps {
  children: ReactNode
  // This component watches for changes on currentPage and reacts to those
  currentPage: null | string
}

interface ActiveContext {
  active: null | string
  setActive: (key: string) => void
}
const defaultContextValue = {
  active: null,
  setActive: () => {},
}
const activeContext = createContext<ActiveContext>(defaultContextValue)

const AsideListInner = function AsideList({
  className,
  ...listProps
}: Omit<Props, "currentPage">) {
  const navListRef = useRef<HTMLUListElement>(null)

  useEffect(function makeCurrentItemVisible() {
    if (navListRef.current) {
      const activeElement = navListRef.current.querySelector(
        '[data-active="true"]'
      )

      if (activeElement) {
        activeElement.scrollIntoView({
          block: "center",
          behavior: "instant",
        })
      }
    }
  }, [])

  return (
    <Stack
      as="ol"
      gap="none"
      ref={navListRef}
      className={[asideList, className]}
      {...listProps}
    />
  )
}

export function AsideList({ currentPage, ...props }: Props) {
  const [activeElement, setActiveElement] = useState<null | string>(currentPage)

  return (
    <activeContext.Provider
      value={{ active: activeElement, setActive: setActiveElement }}
    >
      <AsideListInner {...props} />
    </activeContext.Provider>
  )
}

export const AsideListItem = ({
  id,
  href,
  isShallow,
  children,
  ...props
}: Pick<LinkProps, "href" | "children"> & {
  isShallow?: boolean
  id: string
}) => {
  const { active, setActive } = useContext(activeContext)
  const isCurrentPage = active === id

  return (
    <ListItem
      data-id={id}
      className={asideListItem}
      data-active={isCurrentPage}
      width="100%"
      {...props}
    >
      <Link
        href={href}
        selected={isCurrentPage}
        className={asideListItemLink}
        prefetch={false}
        shallow={isShallow}
        onClick={() => {
          setActive(id)
        }}
      >
        {children}
      </Link>
    </ListItem>
  )
}
